import React, { useState, useEffect } from 'react';
import './MultiSelect.scss';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SelectedChipList from './SelectedChipList.jsx';

// Recursive render function for unlimited nesting
function renderOptions(children, selectedValues, handleOptionToggle, level = 0) {
    return children.map(child => {
        const inputId = `option-${child.value}`;
        return (
            <React.Fragment key={child.value}>
                <div className="option" style={{ marginLeft: level * 12 }}>
                    <input
                        id={inputId}
                        type="checkbox"
                        checked={selectedValues.includes(child.value)}
                        onChange={() => handleOptionToggle(child.value)}
                    />
                    <label htmlFor={inputId}>{child.label}</label>
                </div>
                {child.children && renderOptions(child.children, selectedValues, handleOptionToggle, level + 1)}
            </React.Fragment>
        );
    });
}

// Recursively returns all values in a flat array from nested groups/children
function getAllValues(arr) {
    let result = [];
    arr.forEach(item => {
        result.push(item.value);
        if (item.children && item.children.length) {
            result = result.concat(getAllValues(item.children));
        }
    });
    return result;
}


const GroupedMultiSelect = ({ groups, selectedValues, onChange }) => {
    // Debug: log groups and selectedValues
    console.log('GroupedMultiSelect: groups prop:', groups);
    console.log('GroupedMultiSelect: selectedValues:', selectedValues);
    const [isOpen, setIsOpen] = useState(false);
    const [openGroups, setOpenGroups] = useState([]); // All closed by default
    const dropdownRef = React.useRef(null);

    const handleOptionToggle = (value) => {
        const newSelectedValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];
        onChange(newSelectedValues);
    };

    const handleGroupToggle = (group) => {
        // Collect all values from children and nested children
        const collectValues = (children) => {
            return children.flatMap(child =>
                child.children ? [child.value, ...collectValues(child.children)] : [child.value]
            );
        };
        const groupValues = collectValues(group.children);
        const allSelected = groupValues.every((value) => selectedValues.includes(value));
        const newSelectedValues = allSelected
            ? selectedValues.filter((value) => !groupValues.includes(value))
            : [...new Set([...selectedValues, ...groupValues])];
        onChange(newSelectedValues);
    };

    const handleSelectAllToggle = () => {
        const allOptions = getAllValues(groups);
        const allSelected = allOptions.every((value) => selectedValues.includes(value));
        const newSelectedValues = allSelected
            ? selectedValues.filter((value) => !allOptions.includes(value))
            : [...new Set([...selectedValues, ...allOptions])];
        onChange(newSelectedValues);
    };

    const getDisplayText = () => {
        if (!selectedValues || !Array.isArray(selectedValues)) {
            return `Select option`;
        }
        if (selectedValues.length === 0) {
            return `Select option`;
        }
        if (selectedValues.length === 1) {
            return `1 option selected`;
        }
        return `${selectedValues.length} options selected`;
    };
    const isGroupFullySelected = (group) => {
        if (!group || !group.children || !Array.isArray(group.children)) {
            return false;
        }
        const groupValues = group.children.map((child) => child.value);
        return groupValues.every((value) => selectedValues && selectedValues.includes(value));
    };

    const isGroupPartiallySelected = (group) => {
        if (!group || !group.children || !Array.isArray(group.children)) {
            return false;
        }
        const groupValues = group.children.map((child) => child.value);
        return groupValues.some((value) => selectedValues && selectedValues.includes(value)) && !isGroupFullySelected(group);
    };

    // Accordion open/close toggle for groups
    const handleAccordionToggle = (groupValue) => {
        setOpenGroups((prevOpenGroups) =>
            prevOpenGroups.includes(groupValue)
                ? prevOpenGroups.filter((v) => v !== groupValue)
                : [...prevOpenGroups, groupValue]
        );
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !event.target.closest('.multi-select')
            ) {
                setIsOpen(false);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, []);

    return (
        <div>
            <div className="multi-select" ref={dropdownRef}>
                <div
                    className="multi-select-trigger"
                    onClick={() => setIsOpen(!isOpen)}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
                >
                    <span className="multi-select-value">{getDisplayText()}</span>
                    <span className={`multi-select-arrow ${isOpen ? 'open' : ''}`}>▼</span>
                </div>
                {isOpen && (
                    <div className="dropdown-menu">
                        {/* Select All Items option before the first group */}
                        <div className="select-all-items">
                            <input
                                id="select-all-items"
                                type="checkbox"
                                checked={groups.flatMap(group => group.children.map(child => child.value)).every((value) => selectedValues.includes(value))}
                                onChange={handleSelectAllToggle}
                            />
                            <label htmlFor="select-all-items">Select All Items</label>
                        </div>
                        {/* Render each group */}
                        {groups.map((group) => {
                            const isOpenGroup = openGroups.includes(group.value);
                            return (
                                <div key={group.value} className="group">
                                    <div className="group-header">
                                        <input
                                            id={`group-${group.value}`}
                                            type="checkbox"
                                            checked={isGroupFullySelected(group)}
                                            ref={(input) => {
                                                if (input) input.indeterminate = isGroupPartiallySelected(group);
                                            }}
                                            onChange={() => handleGroupToggle(group)}
                                            className='left'
                                        />
                                        <label className='left' htmlFor={`group-${group.value}`}>{group.label}</label>
                                        <span className="group-count count">({group.children.length})</span>
                                        <span
                                            className={`accordion-toggle ${isOpenGroup ? 'open' : ''}`}
                                            onClick={() => handleAccordionToggle(group.value)}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={isOpenGroup ? 'Collapse group' : 'Expand group'}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAccordionToggle(group.value)}
                                        >
                                            {isOpenGroup ? '▼' : '▶'}
                                        </span>
                                    </div>
                                    {isOpenGroup && (
                                        <div className="select-options">
                                            {renderOptions(group.children, selectedValues, handleOptionToggle)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <SelectedChipList
                items={(() => {
                    function findOptionByValue(arr, val) {
                        for (const item of arr) {
                            if (item.value === val) return item;
                            if (item.children) {
                                const found = findOptionByValue(item.children, val);
                                if (found) return found;
                            }
                        }
                        return null;
                    }
                    return selectedValues
                        .map((value) => {
                            const option = findOptionByValue(groups, value);
                            return option ? { value, label: option.label } : null;
                        })
                        .filter(Boolean);
                })()}
                onRemove={handleOptionToggle}
            />
        </div>
    );
}

export default GroupedMultiSelect;
